/**
 * Context-aware lighting: the light question asked in the language of what
 * you're shooting. Someone photographing a plate of food thinks "restaurant,
 * kind of dim", not "EV 5" — so each subject gets its own situations, each
 * mapping to a brightness the solver understands.
 *
 * EV reference points (at ISO 100): 15 bright sun, 12 overcast, 11 open
 * shade, 9 low sun, 8 bright interior, 6 lamp-lit room, 5 restaurant,
 * 4 night street, 3 dim night.
 */

export interface LightSituation {
  id: string
  label: string
  hint: string
  ev: number
}

interface SubjectLighting {
  /** The question header, phrased for this subject. */
  question: string
  options: LightSituation[]
  /** Which option to pre-select. */
  defaultId: string
}

const OUTDOOR_BASICS: LightSituation[] = [
  { id: 'sun', label: 'Bright sun', hint: 'Hard light, sharp shadows', ev: 15 },
  { id: 'overcast', label: 'Cloudy', hint: 'Soft grey daylight', ev: 12 },
  { id: 'lowsun', label: 'Low sun', hint: 'Late in the day — warm, noticeably dim', ev: 9 },
]

export const LIGHTING_BY_SUBJECT: Record<string, SubjectLighting> = {
  buildings: {
    question: "What's the light on the building?",
    defaultId: 'overcast',
    options: [
      ...OUTDOOR_BASICS,
      { id: 'bluehour', label: 'Dusk / blue hour', hint: 'Sky still glowing, lights coming on', ev: 6 },
      { id: 'nightlit', label: 'Lit up at night', hint: 'Floodlights, signage, windows glowing', ev: 4 },
    ],
  },
  landscape: {
    question: "What's the light on the scene?",
    defaultId: 'overcast',
    options: [
      ...OUTDOOR_BASICS,
      { id: 'goldenhour', label: 'Golden hour', hint: 'Sun nearly down, everything warm', ev: 9 },
      { id: 'bluehour', label: 'Dusk / blue hour', hint: 'Sun gone, sky still glowing', ev: 6 },
    ],
  },
  street: {
    question: "What's the street like right now?",
    defaultId: 'overcast',
    options: [
      { id: 'sun', label: 'Sunny street', hint: 'Hard light, deep shadows between buildings', ev: 15 },
      { id: 'overcast', label: 'Grey day', hint: 'Soft, even light everywhere', ev: 12 },
      { id: 'shade', label: 'In the shadows', hint: 'Narrow streets, covered arcades', ev: 11 },
      { id: 'dusk', label: 'Dusk', hint: 'Lights coming on, sky fading', ev: 6 },
      { id: 'night', label: 'Night', hint: 'Shop windows and streetlights', ev: 4 },
    ],
  },
  food: {
    question: "Where's the food?",
    defaultId: 'window',
    options: [
      { id: 'patio', label: 'Outdoors', hint: 'Patio or market stall, daylight', ev: 12 },
      { id: 'window', label: 'By a window', hint: 'Daylight falling on the table', ev: 8 },
      { id: 'restaurant', label: 'Restaurant', hint: 'Typical indoor dining light', ev: 5 },
      { id: 'candlelit', label: 'Dim & moody', hint: 'Candles, low warm lamps', ev: 4 },
    ],
  },
  action: {
    question: "Where's the action?",
    defaultId: 'sun',
    options: [
      { id: 'sun', label: 'Outdoors, sunny', hint: 'Field or court in full daylight', ev: 15 },
      { id: 'overcast', label: 'Outdoors, cloudy', hint: 'Grey sky over the pitch', ev: 12 },
      { id: 'floodlit', label: 'Floodlit', hint: 'Evening game under lights', ev: 7 },
      { id: 'gym', label: 'Indoor gym or hall', hint: 'Ceiling lights, no daylight', ev: 6 },
    ],
  },
  kids: {
    question: 'Where are they playing?',
    defaultId: 'overcast',
    options: [
      { id: 'sun', label: 'Outside, sunny', hint: 'Park or garden in full sun', ev: 15 },
      { id: 'overcast', label: 'Outside, cloudy', hint: 'Playground on a grey day', ev: 12 },
      { id: 'daylight-room', label: 'Bright room', hint: 'Daytime, big windows', ev: 8 },
      // Lamp-only rooms meter EV 4-5; EV 6 needs ceiling lights on.
      { id: 'evening-room', label: 'Evening indoors', hint: 'Lamps on, curtains shut', ev: 5 },
    ],
  },
  portrait: {
    question: "What's the light on them?",
    defaultId: 'shade',
    options: [
      { id: 'sun', label: 'Full sun', hint: 'Squinting weather — watch for harsh shadows', ev: 15 },
      { id: 'shade', label: 'Open shade', hint: 'Under a tree or awning — flattering', ev: 11 },
      { id: 'goldenhour', label: 'Golden hour', hint: 'Low warm sun, the classic look', ev: 9 },
      { id: 'window', label: 'By a window', hint: 'Indoors, daylight on their face', ev: 8 },
      // Lamp-only rooms meter EV 4-5; the 'indoors' subject's EV 6 option is
      // ceiling-lights-on, which is genuinely a stop brighter.
      { id: 'evening-room', label: 'Evening indoors', hint: 'Lamplight only', ev: 5 },
    ],
  },
  indoors: {
    question: "What's the room like?",
    defaultId: 'evening-room',
    options: [
      { id: 'daylight-room', label: 'Daytime, bright room', hint: 'Big windows, lights off', ev: 8 },
      { id: 'evening-room', label: 'Evening, lights on', hint: 'Ceiling lights and lamps', ev: 6 },
      { id: 'party-dim', label: 'Party lighting', hint: 'Dimmed right down, candles on the cake', ev: 4 },
    ],
  },
}

/** Fallback for any subject without a tailored list. */
const GENERIC: SubjectLighting = {
  question: "How's the light?",
  defaultId: 'overcast',
  options: [
    ...OUTDOOR_BASICS,
    { id: 'indoor', label: 'Indoors', hint: 'Lamps and ceiling lights', ev: 6 },
    { id: 'night', label: 'Night', hint: 'Streetlights and windows', ev: 4 },
  ],
}

export function lightingFor(subjectId: string): SubjectLighting {
  return LIGHTING_BY_SUBJECT[subjectId] ?? GENERIC
}
